import React, { useCallback, useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, Transition } from '@headlessui/react'

export const Prompt = (props) => {
  const { when, onOK, onCancel, title, message, okText, cancelText } = props;

  const history = useNavigate();

  const [showPrompt, setShowPrompt] = useState(false);
  const [currentPath, setCurrentPath] = useState("");


  useEffect(() => {
    if (when) {
      history.block((prompt) => {
        setCurrentPath(prompt.pathname);
        setShowPrompt(true);
        return "true";
      });
    } else {
      history.block(() => {});
    }

    return () => {
      history.block(() => {});
    };
  }, [history, when]);

  const handleOK = useCallback(async () => {
    if (onOK) {
      const canRoute = await Promise.resolve(onOK());
      if (canRoute) {
        history.block(() => {});
        history.push(currentPath);
      }
    }
  }, [currentPath, history, onOK]);

  const handleCancel = useCallback(async () => {
    if (onCancel) {
      const canRoute = await Promise.resolve(onCancel());
      if (canRoute) {
        history.block(() => {});
        history.push(currentPath);
      }
    }
    setShowPrompt(false);
  }, [currentPath, history, onCancel]);

  return showPrompt ? (
    <Transition appear show={showPrompt} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleOK}>
        <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
            >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                >
                    { title }
                </Dialog.Title>
                <div className="mt-2">
                    <p className="text-sm text-gray-500">
                        { message }
                    </p>
                </div>

                <div className="mt-4">
                    <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleOK}
                    >
                    Got it, thanks!
                    </button>

                    <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleCancel}
                    >
                    Cancel
                    </button>
                </div>
                </Dialog.Panel>
            </Transition.Child>
            </div>
        </div>
        </Dialog>
    </Transition>
  ) : null;
}

/**
 * Modal confirmation (e.g. destructive actions). Controlled with `open` — does not use router block.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  confirmButtonClassName = "inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2",
  cancelButtonClassName = "inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
}) {
  if (!open) return null;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[3600]" onClose={() => onClose && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-[3600] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-md transform rounded-t-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-left shadow-xl transition-all sm:rounded-2xl sm:p-6 max-h-[min(90vh,100%)] overflow-y-auto sm:my-0">
              <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900 pr-2">
                {title}
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{message}</p>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                <button type="button" className={cancelButtonClassName} onClick={() => onClose && onClose()}>
                  {cancelText}
                </button>
                <button
                  type="button"
                  className={confirmButtonClassName}
                  onClick={() => {
                    if (onConfirm) onConfirm();
                  }}
                >
                  {confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

const unsavedBtnBase =
  "inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

/**
 * Three-way confirm when closing a form/dialog with unsaved work.
 */
export function UnsavedChangesDialog({
  open,
  onKeepEditing,
  onCloseWithoutSaving,
  onSave,
  title,
  message,
  keepEditingText,
  closeWithoutSavingText,
  saveText,
  isSaving = false,
}) {
  if (!open) return null;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[3600]" onClose={() => onKeepEditing && onKeepEditing()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 z-[3600] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-md transform rounded-t-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] text-left shadow-xl transition-all sm:rounded-2xl sm:p-6">
              <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900 pr-2">
                {title}
              </Dialog.Title>
              <div className="mt-2">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{message}</p>
              </div>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  className={`${unsavedBtnBase} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-blue-500`}
                  onClick={() => onKeepEditing && onKeepEditing()}
                >
                  {keepEditingText}
                </button>
                <button
                  type="button"
                  className={`${unsavedBtnBase} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-blue-500`}
                  onClick={() => onCloseWithoutSaving && onCloseWithoutSaving()}
                >
                  {closeWithoutSavingText}
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  className={`${unsavedBtnBase} border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500 disabled:opacity-60`}
                  onClick={() => onSave && onSave()}
                >
                  {saveText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
